import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import ManageCategoryModal from '../../../../components/ManageCategoryModal/ManageCategoryModal';
import { writeBatch } from '../../../../libs/firestore-wrapper';
import { getUserMenuCategoryDetails, getFoodDetails } from '../../../../modals/order';
import { useCategoriesContext } from '../../../../context/CategoriesContext';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { LANG_TEXT } from '../../../../constants/language';
import { IUserMenuCategory } from '../../categoriesManagement.types';
import { addCategoriesToUserMenuList, deleteCategoryFromAllUserMenuLists } from '../../../../context/UserMenuContext/userMenu.services';

type SetToastState = React.Dispatch<React.SetStateAction<{ type: 'error' | 'success'; content: string }>>;

interface CategoriesManagerProps {
  open: boolean;
  handleClose: () => void;
  categories: IUserMenuCategory[];
  setOpenToast: React.Dispatch<React.SetStateAction<boolean>>;
  setToast: SetToastState;
}

interface FormValues {
  [key: string]: string;
}

export const CategoriesManager: React.FC<CategoriesManagerProps> = ({ open, handleClose, categories, setOpenToast, setToast }) => {
  const { control, handleSubmit, getValues, reset } = useForm<FormValues>();
  const { t } = useTranslation();
  const { foodMenu, refetchData } = useCategoriesContext();
  const { userInfo } = useAuthContext();

  const [editStates, setEditStates] = useState<{ [key: string]: { isEdit: boolean; isNew: boolean } }>({});
  const [id, setId] = useState<string>('');
  const [initialCategories, setInitialCategories] = useState<IUserMenuCategory[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [warningTitle, setWarningTitle] = useState<string>('');
  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const handleOpenWarningModal = () => setOpenWarningModal(true);
  const handleCloseWarningModal = () => setOpenWarningModal(false);

  const removeUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
    const copy: Partial<T> = { ...obj };
    Object.keys(copy).forEach((k) => {
      if (copy[k] === undefined) delete copy[k];
    });
    return copy;
  };

  useEffect(() => {
    if (open) {
      setInitialCategories(categories);
      setEditStates({});
    }
  }, [open, categories]);

  const handleSwitchChange = (categoryId: string, isChecked: boolean) => {
    const switchCategories = initialCategories.map((category) => {
      if (category.id === categoryId) {
        return { ...category, enabled: isChecked };
      }
      return category;
    });

    setInitialCategories(switchCategories);
  };

  const handleEditClick = (category: any, status: boolean) => {
    const categoryId = category.id;
    if (status) {
      reset({ [`category_${categoryId}`]: category.title });
    }
    const editState = editStates[categoryId];
    if (editState?.isEdit === true && editState?.isNew === true) {
      const updatedCategories = initialCategories.filter((category) => category.id !== categoryId);

      setInitialCategories(updatedCategories);
      setEditStates((prevStates) => {
        const { [categoryId]: _, ...rest } = prevStates;
        return rest;
      });

      return;
    }

    setEditStates((prevStates) => ({
      ...prevStates,
      [categoryId]: {
        isEdit: status,
        isNew: false,
      },
    }));
  };

  const handleAddNewCategory = () => {
    const newCategoryId = uuidv4();
    const order = initialCategories.length;
    const newCategory: IUserMenuCategory = {
      id: newCategoryId.toString(),
      title: '',
      foodIds: [],
      order: order,
      stadium_id: userInfo.stadium_id,
      enabled: true,
    };
    setInitialCategories([newCategory, ...initialCategories]);
    setEditStates((prevStates) => ({
      ...prevStates,
      [newCategoryId]: {
        isEdit: true,
        isNew: true,
      },
    }));
  };

  const handleDelete = () => {
    const categoryIndex = initialCategories.findIndex((category) => category.id === id);

    if (categoryIndex !== -1) {
      const newCategories = [...initialCategories.slice(0, categoryIndex), ...initialCategories.slice(categoryIndex + 1)];

      setInitialCategories(newCategories);
    }
    handleCloseModal();
  };

  const handleCheckIconClick = (categoryId: string) => {
    const newTitle = getValues(`category_${categoryId}`);
    const isDuplicate = initialCategories.some((category) => category.id !== categoryId && category.title.toLowerCase() === newTitle.toLowerCase());

    if (isDuplicate) {
      setWarningTitle(t(LANG_TEXT.WARNING_DUPLICATE_TITLE));
      handleOpenWarningModal();
      return;
    }

    const updatedCategories = initialCategories.map((category) => {
      if (category.id === categoryId) {
        return { ...category, title: newTitle };
      }
      return category;
    });

    setInitialCategories(updatedCategories);
    setEditStates((prevStates) => ({
      ...prevStates,
      [categoryId]: {
        isEdit: false,
        isNew: false,
      },
    }));
  };

  const onSubmit = handleSubmit(async () => {
    const validateTitle = initialCategories.some((category) => !category.title || !category.title.trim());
    const isEditCategogy = Object.values(editStates).some((value) => value.isEdit === true);

    if (validateTitle || isEditCategogy) {
      setWarningTitle(t(LANG_TEXT.CONTINUE_UPDATE_TEXT));
      handleOpenWarningModal();
      return;
    }

    const batch = writeBatch();
    const updatedIds = new Set(initialCategories.map((category) => category.id));
    const initialIds = new Set(categories.map((category) => category.id));
    const deletedIds = categories.filter((category) => !updatedIds.has(category.id)).map((category) => category.id);
    const addedIds = initialCategories.filter((category) => !initialIds.has(category.id)).map((category) => category.id);

    const hasStructuralChanges = deletedIds.length > 0 || addedIds.length > 0;

    const hasContentChanges = initialCategories.some((cat) => {
      const originalCat = categories.find((c) => c.id === cat.id);
      if (!originalCat) return false;
      return cat.title !== originalCat.title || cat.order !== originalCat.order || cat.enabled !== originalCat.enabled;
    });

    if (!hasStructuralChanges && !hasContentChanges) {
      handleClose();
      return;
    }

    deletedIds.forEach((deletedCategoryId) => {
      const categoryRef = getUserMenuCategoryDetails(deletedCategoryId);
      batch.delete(categoryRef);

      const deletedCategory = categories.find((cat) => cat.id === deletedCategoryId);
      const affectedFoodIds = deletedCategory?.foodIds || [];

      affectedFoodIds.forEach((foodId) => {
        const food = foodMenu.foods.find((f) => f.id === foodId);
        if (food && food.categories_menu_ids) {
          const updatedCategories = food.categories_menu_ids.filter((cat: any) => {
            const catId = typeof cat === 'string' ? cat : cat.id;
            return catId !== deletedCategoryId;
          });
          const foodRef = getFoodDetails(foodId);
          batch.update(foodRef, { categories_menu_ids: updatedCategories });
        }
      });
    });

    const reorderedCategories = initialCategories.map((category, index) => ({
      ...category,
      order: index,
    }));

    reorderedCategories.forEach((category) => {
      const foodDetailsRef = getUserMenuCategoryDetails(category.id);
      batch.set(
        foodDetailsRef,
        removeUndefined({
          ...category,
          order: category.order,
        }),
        { merge: true }
      );
    });

    try {
      await batch.commit();

      if (userInfo?.stadium_id) {
        if (addedIds.length > 0) {
          const baseOrder = categories?.length || 0;
          await addCategoriesToUserMenuList(
            userInfo.stadium_id,
            addedIds.map((c, i) => ({ id: c, order: baseOrder + i }))
          );
        }

        for (const deletedId of deletedIds) {
          await deleteCategoryFromAllUserMenuLists(userInfo.stadium_id, deletedId);
        }
      }

      await refetchData();

      handleClose();
      setToast({
        content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE),
        type: 'success',
      });
      setOpenToast(true);
    } catch (error) {
      setToast({
        content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE),
        type: 'error',
      });
      setOpenToast(true);
    }
  });

  const moveCategory = (dragIndex: number, hoverIndex: number) => {
    const draggedCategory = initialCategories[dragIndex];
    const updatedCategories = [...initialCategories];
    updatedCategories.splice(dragIndex, 1);
    updatedCategories.splice(hoverIndex, 0, draggedCategory);
    const reindexed = updatedCategories.map((c, idx) => ({ ...c, order: idx }));
    setInitialCategories(reindexed);
  };

  const handleDeleteRequest = (categoryId: string) => {
    handleOpenModal();
    setId(categoryId);
  };

  return (
    <ManageCategoryModal
      open={open}
      handleClose={handleClose}
      categories={initialCategories}
      editStates={editStates}
      control={control}
      onSubmit={onSubmit}
      onSwitchChange={handleSwitchChange}
      onEditClick={handleEditClick}
      onCheckIconClick={handleCheckIconClick}
      onAddNewCategory={handleAddNewCategory}
      onMoveCategory={moveCategory}
      onDeleteRequest={handleDeleteRequest}
      onDeleteConfirm={handleDelete}
      deleteModalOpen={openModal}
      onDeleteModalClose={handleCloseModal}
      warningModalOpen={openWarningModal}
      warningTitle={warningTitle}
      onWarningModalClose={handleCloseWarningModal}
    />
  );
};

export default CategoriesManager;
